import { styled } from "@linaria/react";
import type { ChangeEvent } from "react";
import React from "react";
import { useDelete, usePost } from "../../../../api/useCall";
import { useManualFailures } from "../../../../api/useManualFailures";
import type { Project } from "../../../../api/useProjects";
import t from "../../../../i18n";
import useToasts from "@ui/Toast/useToasts";
import { color, fontSizes } from "@ui";
import Button from "@ui/Button";
import { DeleteBinLine, FileCopyLine } from "@ui/IconV2/react";
import Input from "@ui/Input/InputV2";
import ModalForm from "@ui/Modal/Form";
import ReactSelect from "@ui/ReactSelect";
import Select from "@ui/Select";
import { fonts } from "@ui/Theme";
import type { RawManualFailureValue, Reparability } from "../../../../api/useManualFailureValues";
import { useManualFailureValueAnalysisIdIndex } from "../../../../api/useManualFailureValues";
import { useQuantities } from "../../../../api/useQuantities";
import formatDate from "@ui/i18n/formatDate";
import { comparatorBy } from "diagnostics-lib/comparator";
import { analysisTitleLong } from "../../Analysis/analysisTitle";
import { useMutateBody } from "../../../../api/useQuery";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { useTranslations } from "../../../../api/useTranslations";
import { isLeft } from "fp-ts/lib/Either";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import { useSettings } from "../../../../api/useSettings";

const Cell = styled.div`
  display: flex;
  flex-grow: 1;
  flex-basis: 0;
  min-width: 0;

  & > :first-child {
    width: 100%;
  }

  .reactSelect > div {
    height: 42px;
  }
`;

const NameCell = styled(Cell)`
  display: flex;
  flex-direction: column;
  flex-grow: 1.5;
`;

const DeleteCell = styled(Cell)`
  flex-grow: 0;
  flex-basis: 30px;
`;

const QOICell = styled(Cell)`
  flex-grow: 1;
`;

const ReparabilityCell = styled(Cell)`
  flex-grow: 1.5;
`;

const AnalysisIdsCell = styled(Cell)`
  flex-grow: 2;
`;

const Subtitle = styled.h4`
  color: ${color.darkNitrogen900};
  font: ${fontSizes[10]};
  font-weight: 600;
`;

const StyleButtonIcon = styled(ButtonIconV2)`
  > :first-child {
    transform: translateY(0.2em);
  }

  svg {
    position: relative;
    top: -2px;
    left: -1px;
  }

  margin-left: auto;
  width: 20px;
  color: ${color.carbon800};
`;

const GAP_PX = 12;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${GAP_PX}px;
`;

const Line = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0 ${GAP_PX}px;
  gap: ${GAP_PX}px;
`;

const Header = styled(Line)`
  align-items: center;
  color: ${color.carbon800};
  background-color: ${color.titanium100};
  font: ${fonts.body2SemiBold};
  height: 50px;
`;

const FormColumnDeleteButton = styled(Cell)`
  align-self: center;
  justify-content: flex-end;
`;

const StyledButton = styled(Button)`
  margin-right: auto;
  margin-left: ${GAP_PX}px;
`;

interface P {
  closeModal: () => void;
  selectedAnalyses: ActiveAnalysis[];
  activeAnalyses: ActiveAnalysis[];
  boundary: {
    pivot: number | null;
    to: number | null;
  };
  project: Project;
}

interface Impact {
  quantityId: string;
  value: number | undefined;
}

const reparabilities = ["REPARABILITY_0", "REPARABILITY_1", "REPARABILITY_2"] as const;

interface Entry {
  id: string;
  persisted: boolean;
  manualFailureId: string;
  analysisIds: string[];
  impacts: Impact[];
  reparability: Reparability;
}

function getEntryHash(manualFailureId: string, reparability: Reparability, impacts: Impact[]) {
  return `${manualFailureId}-${reparability ?? "null"}-${
    impacts.map(i => i.value).join("|") ?? "null"
  }`;
}

const computeEntries = (
  manualFailureValues: RawManualFailureValue[],
  qoiIds: string[],
): Entry[] => {
  const entries: Record<string, Entry> = {};
  for (const manualFailureValue of manualFailureValues) {
    const impacts = qoiIds.map(qoiId => ({
      quantityId: qoiId,
      value: manualFailureValue.manualFailureImpacts.find(i => i.quantityId === qoiId)?.value ?? 0,
    }));
    const entryHash = getEntryHash(
      manualFailureValue.manualFailureId,
      manualFailureValue.reparability,
      impacts,
    );
    if (entries[entryHash] === undefined) {
      entries[entryHash] = {
        id: entryHash,
        persisted: true,
        manualFailureId: manualFailureValue.manualFailureId,
        reparability: manualFailureValue.reparability,
        analysisIds: [manualFailureValue.analysisId],
        impacts,
      };
    } else {
      entries[entryHash]!.analysisIds.push(manualFailureValue.analysisId);
    }
  }

  return Object.values(entries).sort(comparatorBy("id"));
};

let index = 0;

const ManualFailuresModalForm = ({
  closeModal,
  selectedAnalyses,
  activeAnalyses,
  project,
  boundary,
}: P) => {
  const settings = useSettings();
  const translations = useTranslations();
  const projectId = project.id;
  const { addToast } = useToasts();
  const quantities = useQuantities(projectId);
  const selectedAnalysisIndex = React.useMemo(
    () =>
      selectedAnalyses[0]
        ? activeAnalyses.findIndex(a => a.date === selectedAnalyses[0]!.date)
        : -1,
    [activeAnalyses, selectedAnalyses],
  );
  const previousAnalysis = activeAnalyses[selectedAnalysisIndex - 1];

  const qois = React.useMemo(() => quantities.filter(q => q.qoiOrder !== null), [quantities]);
  const qoiIds = React.useMemo(() => qois.map(qoi => qoi.id), [qois]);
  const manualFailureValueAnalysisIdIndex = useManualFailureValueAnalysisIdIndex(
    projectId,
    !settings.MANUAL_EVENTS_DEV_MODE_ENABLED,
  );
  const analysisIds = React.useMemo(() => selectedAnalyses.map(a => a.id), [selectedAnalyses]);
  const manualFailureValues = React.useMemo(
    () => analysisIds.flatMap(analysisId => manualFailureValueAnalysisIdIndex[analysisId] ?? []),
    [manualFailureValueAnalysisIdIndex, analysisIds],
  );
  const manualFailures = useManualFailures(projectId);
  const mutateManualFailureValues = useMutateBody("/projects/:projectId(uuid)/manual-failures", {
    routeParams: { projectId },
  });

  const persistedEntries = React.useMemo(
    () => computeEntries(manualFailureValues, qoiIds),
    [manualFailureValues, qoiIds],
  );

  const analysisOptions = React.useMemo(
    () =>
      selectedAnalyses.map(analysis => ({
        value: analysis.id,
        label: analysisTitleLong(analysis, project.timezone),
      })),
    [project.timezone, selectedAnalyses],
  );

  const [entries, setEntries] = React.useState<Entry[]>(() =>
    JSON.parse(JSON.stringify(persistedEntries)),
  );

  const usedManualFailureIds = React.useMemo(() => entries.map(e => e.manualFailureId), [entries]);

  const manualFailureOptions = React.useMemo(
    () =>
      manualFailures
        .map(manualFailure => ({
          value: manualFailure.id,
          label: manualFailure.name,
        }))
        .filter(o => !usedManualFailureIds.includes(o.value)),
    [manualFailures, usedManualFailureIds],
  );

  const dirty = React.useMemo(
    () => JSON.stringify(entries) !== JSON.stringify(persistedEntries),
    [entries, persistedEntries],
  );

  // update entries when persisted entries change and user has not modified them
  React.useEffect(() => {
    if (!dirty) setEntries(persistedEntries);
  }, [dirty, persistedEntries]);

  const valid = React.useMemo(
    () =>
      entries.every(entry => {
        if (entry.manualFailureId === "") return false;
        if (entry.analysisIds.length === 0) return false;
        return true;
      }),
    [entries],
  );

  const createManualFailure = usePost("/projects/:projectId(uuid)/manual-failure");
  const replaceManualFailureValues = usePost(
    "/projects/:projectId(uuid)/manual-failure-values/replace",
  );
  const deletedManualFailure = useDelete("/projects/:projectId(uuid)/manual-failure/:id(uuid)");

  const lowerBoundary = boundary.pivot === null ? "" : formatDate(boundary.pivot, "date", null);
  const upperBoundary = boundary.to === null ? "" : formatDate(boundary.to, "date", null);

  const handleAddEmptyEntry = React.useCallback(() => {
    const newEntry: Entry = {
      id: String(index++),
      persisted: false,
      manualFailureId: "",
      analysisIds,
      impacts: qoiIds.map(qoiId => ({
        quantityId: qoiId,
        value: undefined,
      })),
      reparability: null,
    };
    setEntries([...entries, newEntry]);
  }, [entries, qoiIds, analysisIds]);

  const handleDeleteEntry = React.useCallback(
    (entry: Entry) => (_: React.MouseEvent<HTMLButtonElement>) =>
      setEntries(entries => entries.filter(e => e.id !== entry.id)),
    [],
  );

  const handleImpactChange = React.useCallback(
    (entry: Entry, qoiId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const reversedImpactForDisplay = -1 * event.currentTarget.valueAsNumber;
      return setEntries(entries =>
        entries.map(e =>
          e.id === entry.id
            ? {
                ...e,
                impacts: e.impacts.map(i =>
                  i.quantityId === qoiId
                    ? {
                        ...i,
                        value: reversedImpactForDisplay,
                      }
                    : i,
                ),
              }
            : e,
        ),
      );
    },
    [],
  );

  const handleAnalysisIdsChange = React.useCallback(
    (entry: Entry) => (option: ChangeEvent<HTMLSelectElement>) => {
      const analysisIds = Array.from(option.currentTarget.selectedOptions).map(o => o.value);
      return setEntries(entries =>
        entries.map(e =>
          e.id === entry.id
            ? {
                ...e,
                analysisIds,
              }
            : e,
        ),
      );
    },
    [],
  );

  const handleManualFailureInputChange = React.useCallback((newValue: string) => {
    if (isLeft(short(io.string).decode(newValue))) {
      return newValue.slice(0, 100);
    }
    return newValue;
  }, []);

  const handleManualFailureIdChange = React.useCallback(
    (entry: Entry) => (option: { label: string; value: string } | null) =>
      setEntries(entries =>
        entries.map(e =>
          e.id === entry.id
            ? {
                ...e,
                manualFailureId: option === null ? "" : option.value,
              }
            : e,
        ),
      ),
    [],
  );

  const handleCreateManualFailure = React.useCallback(
    (entry: Entry) => async (text: string) => {
      if (isLeft(short(io.string).decode(text))) return;

      const { body: newManualFailure } = await createManualFailure({
        routeParams: { projectId },
        body: {
          name: text,
          degradationTypeId: null,
          equipmentTypeId: null,
          failureCategoryId: null,
          systemId: null,
          suspectedElementIds: [],
          equipmentIds: [],
        },
      });

      mutateManualFailureValues(mfs => [...mfs, newManualFailure]);

      setEntries(entries =>
        entries.map(e => (e.id === entry.id ? { ...e, manualFailureId: newManualFailure.id } : e)),
      );
    },
    [createManualFailure, mutateManualFailureValues, projectId],
  );

  const handleDeleteManualFailure = React.useCallback(
    (_: Entry) => async (option: { label: string; value: string } | null) => {
      const routeParams = { projectId, id: option?.value ?? "" };

      await deletedManualFailure({
        routeParams,
      });
      setEntries(entries => entries.flatMap(e => (e.manualFailureId === option?.value ? [] : e)));
    },
    [deletedManualFailure, projectId],
  );

  const getAnalysisIdOptions = React.useCallback(
    (entry: Entry) => {
      const entriesWithSameManualFailure = entries.filter(
        e => e.manualFailureId === entry.manualFailureId && e.id !== entry.id,
      );

      return analysisOptions.map(option => {
        const alreadySelectedInAnotherEntry = entriesWithSameManualFailure.some(e =>
          e.analysisIds.includes(option.value),
        );
        return {
          ...option,
          disabled: alreadySelectedInAnotherEntry && !entry.analysisIds.includes(option.value),
        };
      });
    },
    [analysisOptions, entries],
  );

  const handleFormSubmission = React.useCallback(async () => {
    if (!valid) return false;
    await replaceManualFailureValues({
      routeParams: { projectId },
      body: {
        analysisIds,
        entries,
      },
    });

    addToast("success", t("manualFailure.modal.submit.success", analysisIds.length));
    return true;
  }, [addToast, analysisIds, entries, projectId, replaceManualFailureValues, valid]);

  const handleReparabilityChange = React.useCallback(
    (entry: Entry) => (e: ChangeEvent<HTMLSelectElement>) => {
      const reparability =
        e.currentTarget.value === "" ? null : (e.currentTarget.value as Reparability);
      return setEntries(entries =>
        entries.map(e =>
          e.id === entry.id
            ? {
                ...e,
                reparability,
              }
            : e,
        ),
      );
    },
    [],
  );

  const reparabilityOptions = React.useMemo(
    () => reparabilities.map(value => ({ label: t("reparability", value, translations), value })),
    [translations],
  );

  const previousManualFailureValues = React.useMemo(
    () => (previousAnalysis ? (manualFailureValueAnalysisIdIndex[previousAnalysis.id] ?? []) : []),
    [manualFailureValueAnalysisIdIndex, previousAnalysis],
  );
  const previousEntries = React.useMemo(
    () =>
      computeEntries(previousManualFailureValues, qoiIds).map(entry => ({
        ...entry,
        analysisIds,
      })),
    [previousManualFailureValues, qoiIds, analysisIds],
  );

  const copyEnabled = Boolean(previousEntries.length) && entries.length === 0;

  const handlePreviousManualFailureCopy = React.useCallback(() => {
    setEntries(previousEntries);
  }, [previousEntries]);

  return (
    <ModalForm
      onSubmit={dirty && valid ? handleFormSubmission : false}
      onRequestClose={closeModal}
      okButtonName={t("modalForm.button.name.save")}
    >
      <Subtitle>
        {t("manualFailure.modal.subtitle", {
          analysesCount: selectedAnalyses.length,
          lowerBoundary,
          upperBoundary,
        })}
      </Subtitle>
      <StyleButtonIcon
        onClick={copyEnabled ? handlePreviousManualFailureCopy : undefined}
        disabled={!copyEnabled}
      >
        <FileCopyLine size="m" />
      </StyleButtonIcon>

      <Container>
        <Header key="header">
          <NameCell>{t("manualFailure.modal.form.header.name")}</NameCell>
          {qois.map(({ id, name, unit }) => (
            <QOICell key={id}>{t("manualFailure.modal.form.header.qoi", { name, unit })}</QOICell>
          ))}
          <ReparabilityCell>{t("manualFailure.modal.form.header.reparability")}</ReparabilityCell>
          <AnalysisIdsCell>{t("manualFailure.modal.form.header.linkedAnalyses")}</AnalysisIdsCell>
          <DeleteCell />
        </Header>

        {entries.map(entry => (
          <Line key={entry.id}>
            <NameCell>
              <ReactSelect
                className="reactSelect"
                menuPortalTarget={window.document.body}
                options={manualFailureOptions}
                isDisabled={entry.persisted}
                value={{
                  label:
                    manualFailures.find(mf => mf.id === entry.manualFailureId)?.name ??
                    entry.manualFailureId,
                  value: entry.manualFailureId,
                }}
                onInputChange={handleManualFailureInputChange}
                onChange={handleManualFailureIdChange(entry)}
                onCreate={handleCreateManualFailure(entry)}
                onDelete={handleDeleteManualFailure(entry)}
              />
            </NameCell>
            {qoiIds.map(qoiId => {
              const reversedImpactForDisplay = entry.impacts.find(
                i => i.quantityId === qoiId,
              )?.value;
              return (
                <QOICell key={qoiId}>
                  <Input
                    beforeInputSlot={reversedImpactForDisplay ? "-" : undefined}
                    type="number"
                    step="1"
                    min="0"
                    value={
                      reversedImpactForDisplay === undefined
                        ? undefined
                        : reversedImpactForDisplay * -1
                    }
                    onChange={handleImpactChange(entry, qoiId)}
                  />
                </QOICell>
              );
            })}
            <ReparabilityCell>
              <Select
                prompt={t("manualFailure.modal.form.header.reparability")}
                value={entry.reparability ?? ""}
                options={reparabilityOptions}
                onChange={handleReparabilityChange(entry)}
              />
            </ReparabilityCell>
            <AnalysisIdsCell>
              <Select
                multiple
                filtered
                prompt={t("manualFailure.modal.form.everyAnalyses", {
                  analysesCount: entry.analysisIds.length,
                })}
                forcePromptWhenOptionSelected
                options={getAnalysisIdOptions(entry)}
                value={entry.analysisIds}
                onChange={handleAnalysisIdsChange(entry)}
              />
            </AnalysisIdsCell>
            {/* Delete */}
            <DeleteCell>
              <FormColumnDeleteButton>
                <ButtonIconV2 onClick={handleDeleteEntry(entry)}>
                  <DeleteBinLine size="s" />
                </ButtonIconV2>
              </FormColumnDeleteButton>
            </DeleteCell>
          </Line>
        ))}
      </Container>
      <StyledButton variant="link" onClick={handleAddEmptyEntry}>
        {t("manualFailure.modal.form.add.line")}
      </StyledButton>
    </ModalForm>
  );
};

export default ManualFailuresModalForm;
